"use client";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Inquiries() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const faqs = [{
    question: "How does Bedrock approach fire protection for high-hazard industrial facilities?",
    answers: ["We begin with a rigorous site-specific hazard analysis, mapping your operational processes against international fire codes and insurance underwriting requirements. Our engineering team identifies critical vulnerabilities in your suppression and detection systems before they become liabilities.", "Our methodology integrates structural fire resistance with advanced suppression technology, ensuring that your facility meets both regulatory compliance and the stringent resilience standards required by global insurers."]
  }, {
    question: "Can you assist with insurance underwriting and risk mitigation strategies?",
    answers: ["Yes. We act as a technical bridge between your facility operations and your insurance underwriters. By providing detailed, data-backed risk assessments, we help you demonstrate the efficacy of your safety systems, often leading to more favorable underwriting terms.", "We translate complex engineering data into clear, actionable reports that satisfy the technical requirements of insurance loss control departments, reducing the friction in your renewal cycles."]
  }, {
    question: "What industries does Bedrock Safety Engineering primarily serve?",
    answers: ["We specialize in high-stakes environments including large-scale industrial manufacturing, aerospace assembly, and pharmaceutical production. These sectors require a level of precision and regulatory adherence that standard fire protection firms are often unequipped to provide.", "Our team is deeply familiar with the unique fire risks associated with chemical processing, clean-room environments, and high-value aerospace infrastructure."]
  }, {
    question: "How do you ensure compliance with evolving international safety standards?",
    answers: ["Our engineers maintain active participation in NFPA, ISO, and regional regulatory committees. We don't just apply current codes; we anticipate shifts in the regulatory landscape to ensure your facility remains compliant for the long term.", "We provide ongoing advisory services to ensure that facility modifications or process changes are vetted against the latest safety standards before implementation."]
  }, {
    question: "Do you provide support for existing facility retrofits?",
    answers: ["Absolutely. Retrofitting fire protection systems in active industrial environments requires a surgical approach to minimize operational downtime. We design solutions that integrate seamlessly with your existing infrastructure while upgrading your safety posture.", "We prioritize phased implementation strategies that allow your facility to maintain production continuity while we execute critical safety upgrades."]
  }, {
    question: "What is the typical engagement process for a new client?",
    answers: ["The process starts with a technical discovery session to understand your facility's risk profile and operational goals. We then conduct a comprehensive site audit to establish a baseline of your current safety systems.", "Following the audit, we deliver a strategic roadmap that prioritizes remediation and upgrades based on risk severity, regulatory urgency, and your capital expenditure planning."]
  }, {
    question: "How does Bedrock handle data security for sensitive facility blueprints?",
    answers: ["We treat your facility documentation with the same rigor as our engineering designs. All sensitive data is stored in encrypted, access-controlled environments, and we adhere to strict non-disclosure protocols for all client information.", "Our internal systems are audited regularly to ensure that your proprietary operational data remains secure throughout the duration of our engagement."]
  }, {
    question: "Are your engineering solutions vendor-neutral?",
    answers: ["Yes. Bedrock Safety Engineering maintains complete independence from equipment manufacturers and installers. Our recommendations are based solely on the technical requirements of your facility and the most effective path to risk mitigation.", "This neutrality ensures that you receive unbiased advice, allowing you to procure the best-fit technology for your specific operational needs without vendor bias."]
  }, {
    question: "How do we initiate a consultation for our facility?",
    answers: ["You can reach out through our contact portal to schedule an initial technical review. We will assign a lead engineer to discuss your specific challenges and determine how our expertise aligns with your safety objectives.", "We do not provide generic quotes; every engagement is tailored to the specific scale and complexity of your industrial operations."]
  }];
  return <section data-section-id="11381" id="common-inquiries" className="bg-background text-foreground py-28 md:py-36 border-t border-border">
      <div className="mx-auto max-w-[1280px] px-6 md:px-8 lg:px-12">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <div className="lg:col-span-8 lg:col-start-3 text-center">
            <p className="text-xs md:text-sm font-bold uppercase tracking-[0.15em] text-primary">
              § 04 Technical Dossier
            </p>
            <h2 className="font-serif font-semibold text-foreground mt-4 leading-[1.2]" style={{
            fontSize: "clamp(2rem, 4vw, 3rem)",
            letterSpacing: "-0.01em"
          }}>
              Answers to your <em className="italic font-normal text-primary">critical inquiries</em>
            </h2>
            <p className="mt-6 mx-auto text-foreground/80 leading-[1.6]" style={{
            fontSize: "clamp(1.125rem, 1.2vw, 1.25rem)",
            maxWidth: "60ch"
          }}>
              From regulatory compliance to high-hazard risk mitigation, these insights address the core questions industrial leaders raise when securing their operations.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 mt-16">
          <div className="lg:col-span-8 lg:col-start-3">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={i} value={`item-${i}`} className="border-b border-border last:border-b-0">
                  <AccordionTrigger className="py-8 text-left text-foreground font-semibold hover:no-underline data-[state=open]:[&_.faq-question]:text-primary" style={{
                letterSpacing: "0"
              }}>
                    <span className="faq-question pr-4 text-foreground font-default" style={{
                  fontSize: "clamp(1.25rem, 2vw, 1.75rem)",
                  lineHeight: 1.3
                }}>
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pt-2">
                    <div className="space-y-4">
                      {faq.answers.map((paragraph, j) => <p data-index={j} key={j} className="text-foreground/80 leading-[1.6] font-default" style={{
                    fontSize: "1rem",
                    maxWidth: "64ch"
                  }}>
                          {paragraph}
                        </p>)}
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 mt-16">
          <div className="lg:col-span-8 lg:col-start-3 text-center">
            <p className="text-foreground/70 font-default" style={{
            fontSize: "0.875rem",
            letterSpacing: "0.01em",
            lineHeight: 1.45
          }}>
              Need a technical consultation?{" "}
              <Link to="/ContactPage" className="text-primary font-bold underline underline-offset-4 decoration-1 hover:text-blue-600 transition-colors duration-200">
                Begin your assessment.
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>;
}